import { combineEpics } from 'redux-observable';
import { concat, from, of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';

import { toPurchaseStatusesString } from '@modules/advancement/constants';
import { advancementReportRequest } from '@modules/advancementReport';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { youthAdvRequirementsParamsSel } from '@modules/youthProfile/duck/selectors';
import {
  advancementTypes,
  historyItemsTypes,
  personGuidSel,
  updateRequests,
} from '@shared';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';
import { requestStorage } from '@utils';
import { catchAndReport } from '@utils/rxjs/operators';

import {
  UPDATE_ADVANCEMENT_REQUEST,
  UPDATE_ADV_SUMMARY_REQUEST,
  updateActivityResponse,
  updateAdvancementError,
  updateAdvancementResponse,
  updateOfflineAdvancement,
} from './actions';
import { advancementSel, isOfflineOperationSel, personSel } from './selectors';
import services from './services';

const updateOfflineItem = async ({ itemId, date }, state) => {
  const personGuid = personGuidSel(state);
  const requests = (await requestStorage.getRequests(personGuid)).map(item =>
    item.id == itemId ? { ...item, date } : item,
  );
  await requestStorage.setRequests(personGuid, requests);
};

const showSuccessToast = isActivity => {
  const successMsg = intl.formatMessage({
    id: `editAdvancement.EditAdvancementForm.successMsg.${
      isActivity ? 'activity' : 'advancement'
    }`,
  });
  toastService.success(successMsg);
};

const updateOfflineAdvancementDate$ = ({ advancement }, state) => {
  const { completionDate: date } = advancement;

  return from(
    updateOfflineItem(
      {
        itemId: advancement.key,
        date,
      },
      state,
    ),
  ).pipe(
    mergeMap(() => {
      showSuccessToast();
      return concat(of(updateOfflineAdvancement()), of(updateRequests()));
    }),
  );
};

const updateAdvancementDate$ = ({ advancement, person }, state) => {
  const organizationGuid = organizationGuidSel(state);
  const { userId } = person;
  const {
    id: advancementId,
    completionDate: date,
    userAwardId,
    advancementType,
  } = advancement;

  return services
    .updateAdvancementDate$({
      organizationGuid,
      userId,
      advancementType,
      advancementId,
      userAwardId,
      date,
    })
    .pipe(
      mergeMap(() => {
        showSuccessToast();
        return of(
          updateAdvancementResponse({
            ...advancement,
            userId,
          }),
        );
      }),
    );
};

const updateActivityEpic$ = (personActiviy, updatedValues) =>
  services.updateActivity$(personActiviy, updatedValues).pipe(
    mergeMap(updatedResponse => {
      // extract activity values from update or create response, if is update, the response come in an array
      const activityValues = updatedResponse.reduce((acc, updatedValue) => {
        if (Array.isArray(updatedValue)) {
          const [{ activityValues }] = updatedValue;
          return [...acc, ...activityValues];
        } else if (updatedValue) {
          return [...acc, updatedValue];
        }
        return acc;
      }, []);
      const { userId, activityId } = personActiviy;
      showSuccessToast(true);
      return of(
        updateActivityResponse({
          activityId,
          userId,
          updatedRecords: activityValues,
        }),
      );
    }),
  );

const updateAdvancementRequestEpic$ = (action$, state$) =>
  action$.ofType(UPDATE_ADVANCEMENT_REQUEST).pipe(
    switchMap(({ payload }) => {
      const state = state$.value;
      const isRecordedOffline = isOfflineOperationSel(state);
      const advancement = advancementSel(state);
      const person = personSel(state);

      if (historyItemsTypes.ADVANCEMENT === advancement.type) {
        const updatedAdvancement = { ...advancement, ...payload };
        const updateEpic$ = isRecordedOffline
          ? updateOfflineAdvancementDate$
          : updateAdvancementDate$;

        if (
          updatedAdvancement.advancementType === advancementTypes.ADVENTURES &&
          !updatedAdvancement.id
        ) {
          updatedAdvancement.id = updatedAdvancement.adventureId;
        }

        return updateEpic$(
          { advancement: updatedAdvancement, person },
          state,
        ).pipe(catchAndReport(err => of(updateAdvancementError(err))));
      }

      const { personGuid } = person;
      return updateActivityEpic$({ ...advancement, personGuid }, payload).pipe(
        catchAndReport(err => of(updateAdvancementError(err))),
      );
    }),
  );

const showSuccessAdvSummaryToast = () => {
  const successMsg = intl.formatMessage({
    id: 'editAdvancement.EditAdvancementForm.successMsg.advSummary',
  });
  toastService.success(successMsg);
};

//
const updateAdvSummaryEpic$ = (action$, state$) =>
  action$.ofType(UPDATE_ADV_SUMMARY_REQUEST).pipe(
    switchMap(({ payload }) => {
      const state = state$.value;
      const organizationGuid = organizationGuidSel(state);
      const { userId: youthUserId } = youthAdvRequirementsParamsSel(state);
      const {
        newAdvancement,
        advancementType,
        advancementId,
        isFromProfile = true,
        onFail,
      } = payload;
      const userId =
        isFromProfile && youthUserId
          ? youthUserId
          : newAdvancement.awardedUserId;
      // having newStatus in the payload means we will use /unapprove endpoint
      const serviceCall = newAdvancement.newStatus
        ? services.unapproveAdvancement$({ ...newAdvancement })
        : services.updateAdvSummary$({
            userId,
            newAdvancement,
            advancementType,
            advancementId,
          });

      const tagsToInvalidate = [
        tagFactory.AdvancementHistory.unitHistoryByType(
          organizationGuid,
          advancementType,
        ),
        tagFactory.AdvancementHistory.unitHistoryWithStatus(
          organizationGuid,
          toPurchaseStatusesString,
        ),
        tagFactory.OrganizationAdvDashboard.unitDashboard(organizationGuid),
      ];
      if (advancementType === advancementTypes.MERIT_BADGES) {
        tagsToInvalidate.push(tagFactory.YouthMeritBadge.youthMBList(userId));
        tagsToInvalidate.push(
          tagFactory.MeritBadgeUserRequirements.byMeritBadge(
            organizationGuid,
            advancementId,
          ),
        );
      }
      // TODO: add more advancements from RTK until we remove this legacy epic

      return serviceCall.pipe(
        mergeMap(() => {
          showSuccessAdvSummaryToast();

          if (!isFromProfile) {
            return concat(
              of(advancementReportRequest(newAdvancement.organizationGuid)),
              of(
                updateAdvancementResponse({
                  ...payload,
                  userId,
                }),
                esbApi.util.invalidateTags(tagsToInvalidate),
              ),
            );
          }

          return of(
            updateAdvancementResponse({
              ...payload,
              userId,
            }),
            esbApi.util.invalidateTags(tagsToInvalidate),
          );
        }),
        catchAndReport(err => {
          if (onFail) {
            onFail();
          }
          return of(updateAdvancementError(err));
        }),
      );
    }),
  );

export default combineEpics(
  updateAdvSummaryEpic$,
  updateAdvancementRequestEpic$,
);
